import React from 'react';
import ReactDOM from 'react-dom';
import DefaultErrorComponent from './ErrorComponent';
var _skeleton = require('./SkeletonLoading');
var createSkeletonElement = _skeleton.createSkeletonElement;

/**
 * SSR 高阶组件
 * 自动处理服务端数据传递和客户端数据获取
 * 自动从组件静态属性 Component.fetch 获取数据函数
 * 
 * @param {React.Component} WrappedComponent - 要包装的组件
 * @param {Object} options - 配置选项
 * @param {string} options.dataKey - 数据在 props 中的键名，默认 'pageData'
 * @param {boolean} options.showLoadingComponent - 加载时是否显示 Loading 组件
 * @param {React.Component} options.LoadingComponent - 自定义 Loading 组件
 * @param {React.Component} options.ErrorComponent - 自定义错误组件
 * @param {string} options.mountPoint - 客户端挂载点 ID，默认 'react-content'
 * @returns {React.Component} 增强后的组件
 */
function withSSR(WrappedComponent, options = {}) {
  // 从组件的静态属性获取 fetchData
  const fetchData = WrappedComponent.fetch || null;
  const {
    dataKey = 'pageData',
    showLoadingComponent = true,
    LoadingComponent = DefaultLoadingComponent,
    ErrorComponent = DefaultErrorComponent,
    mountPoint = 'react-content'
  } = options;

  function WithSSR(props) {
    const {
      initialData = null,
      renderMode: initialRenderMode = 'csr',
      dataSource: initialDataSource = 'unknown',
      dataError = null
    } = props;

    const [data, setData] = React.useState(initialData);
    const [loading, setLoading] = React.useState(!initialData);
    const [error, setError] = React.useState(dataError);
    const [renderMode, setRenderMode] = React.useState(initialRenderMode);
    const [dataSource, setDataSource] = React.useState(initialDataSource);

    /**
     * 更新客户端 SEO 信息
     * @param {Object} seoData - SEO 数据对象 { title, keywords, description }
     */
    const updateClientSEO = (seoData) => {
      if (typeof window === 'undefined' || !seoData) return;

      // 更新页面标题
      if (seoData.title) {
        document.title = seoData.title;
      }

      // 更新 keywords meta 标签
      if (seoData.keywords) {
        let keywordsMeta = document.querySelector('meta[name="keywords"]');
        if (keywordsMeta) {
          keywordsMeta.setAttribute('content', seoData.keywords);
        } else {
          keywordsMeta = document.createElement('meta');
          keywordsMeta.setAttribute('name', 'keywords');
          keywordsMeta.setAttribute('content', seoData.keywords);
          document.head.appendChild(keywordsMeta);
        }
      }

      // 更新 description meta 标签
      if (seoData.description) {
        let descriptionMeta = document.querySelector('meta[name="description"]');
        if (descriptionMeta) {
          descriptionMeta.setAttribute('content', seoData.description);
        } else {
          descriptionMeta = document.createElement('meta');
          descriptionMeta.setAttribute('name', 'description');
          descriptionMeta.setAttribute('content', seoData.description);
          document.head.appendChild(descriptionMeta);
        }
      }
    };

    /**
     * 获取 URL 查询参数
     */
    const getQueryParams = () => {
      if (typeof window === 'undefined') return {};
      const searchParams = new URLSearchParams(window.location.search);
      const params = {};
      for (const [key, value] of searchParams) {
        params[key] = value;
      }
      return params;
    };

    /**
     * 客户端获取数据
     */
    const fetchDataFunc = async () => {
      setLoading(true);
      setError(null);

      try {
        // 动态导入 ssrDataLoader（仅在客户端需要）
        // 使用相对路径，避免 webpack alias 问题
        const { createDataLoader } = require('../../utils/ssrDataLoader');

        // 创建数据加载器
        const dataLoader = createDataLoader(fetchData, {
          showLoading: true,
          onError: (error) => {
            console.error('Data loading error in withSSR:', error);
          }
        });

        const result = await dataLoader.load({
          query: getQueryParams(),
          params: props.params || {}
        });

        if (result.success) {
          // 如果数据中包含 __seo 字段，更新客户端 SEO
          if (result.data && result.data.__seo) {
            updateClientSEO(result.data.__seo);
            // 从数据中移除 __seo，避免传递给组件
            delete result.data.__seo;
          }
          
          setData(result.data);
          setLoading(false);
          setDataSource('client');
        } else {
          setError(result.error);
          setLoading(false);
        }
      } catch (err) {
        setError(err.message || 'Failed to load data');
        setLoading(false);
      }
    };

    /**
     * 刷新数据
     */
    const refreshData = () => {
      if (fetchData) {
        fetchDataFunc();
      }
    };

    React.useEffect(() => {
      // 如果服务端没有数据，客户端获取
      if (!initialData && fetchData) {
        fetchDataFunc();
      }
    }, []);

    // 如果正在加载且需要显示 Loading 组件
    if (loading && showLoadingComponent) {
      return <LoadingComponent />;
    }

    // 如果有错误且提供了错误组件
    if (error && ErrorComponent) {
      return <ErrorComponent error={error} onRetry={refreshData} />;
    }

    // 构造传递给包装组件的 props
    const enhancedProps = {
      ...props,
      [dataKey]: data,
      loading,
      error,
      renderMode,
      dataSource,
      refreshData
    };

    return <WrappedComponent {...enhancedProps} />;
  }

  // 设置显示名称，方便调试
  const wrappedComponentName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
  WithSSR.displayName = `WithSSR(${wrappedComponentName})`;

  // 将 fetchData 附加为静态属性，方便 Controller 获取
  if (fetchData) {
    WithSSR.fetchData = fetchData;
  }

  // 复制静态属性
  hoistNonReactStatics(WithSSR, WrappedComponent);

  // 客户端自动挂载逻辑
  if (typeof window !== 'undefined') {
    const rootElement = document.getElementById(mountPoint);
    
    if (rootElement) {
      
      // 从 window 对象获取服务端传递的数据
      const initialData = window.__INITIAL_DATA__ || null;
      const renderMode = window.__RENDER_MODE__ || 'csr';
      const dataSource = window.__DATA_SOURCE__ || 'unknown';
      const loadDecision = window.__LOAD_DECISION__ || null;
      const dataError = window.__DATA_ERROR__ || null;

      const props = {
        initialData,
        renderMode,
        dataSource,
        loadDecision,
        dataError,
        controller: null  // 客户端渲染时 controller 为 null
      };

      // 根据渲染模式选择 hydrate 或 render
      if (renderMode === 'ssr' && rootElement.innerHTML.trim()) {
        ReactDOM.hydrate(React.createElement(WithSSR, props), rootElement);
      } else {
        ReactDOM.render(React.createElement(WithSSR, props), rootElement);
      }
    }
  }

  return WithSSR;
}

function DefaultLoadingComponent() {
  return createSkeletonElement(React);
}

/**
 * 复制静态属性（简化版）
 */
function hoistNonReactStatics(targetComponent, sourceComponent) {
  // React 内置静态属性
  const REACT_STATICS = {
    childContextTypes: true,
    contextType: true,
    contextTypes: true,
    defaultProps: true,
    displayName: true,
    getDefaultProps: true,
    getDerivedStateFromError: true,
    getDerivedStateFromProps: true,
    mixins: true,
    propTypes: true,
    type: true
  };

  // 已知需要排除的属性
  const KNOWN_STATICS = {
    name: true,
    length: true,
    prototype: true,
    caller: true,
    callee: true,
    arguments: true,
    arity: true
  };

  const keys = Object.getOwnPropertyNames(sourceComponent);
  
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (!REACT_STATICS[key] && !KNOWN_STATICS[key]) {
      try {
        targetComponent[key] = sourceComponent[key];
      } catch (error) {
        // 某些属性可能无法复制，忽略错误
      }
    }
  }

  return targetComponent;
}

export default withSSR;
